"use client";

import type { ReactNode } from "react";
import { CheckIcon } from "lucide-react";
import { cn } from "../../lib";
import { buildControlDescribedBy, fieldMixedId } from "./field-a11y";
import { FieldShell, type FieldLayoutHint } from "./field-shell";
import type { SingleSelectOption } from "./single-select-field";

export type MultiSelectPresentation = "tiles" | "check_menu";

export interface MultiSelectFieldProps {
	id: string;
	label: string;
	value: string[];
	options: SingleSelectOption[];
	onChange: (value: string[]) => void;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	layoutHint?: FieldLayoutHint;
	presentation?: MultiSelectPresentation;
	className?: string;
	disabled?: boolean;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	mixedPlaceholder?: string;
}

export function MultiSelectField({
	id,
	label,
	value,
	options,
	onChange,
	description,
	error,
	reserveErrorSpace = false,
	name,
	onBlur,
	layoutHint,
	presentation = "tiles",
	className,
	disabled = false,
	mixed = false,
	mixedTooltip,
	mixedPlaceholder = "Mixed values",
}: MultiSelectFieldProps) {
	const mixedId = fieldMixedId(id);
	const controlDescribedBy = buildControlDescribedBy({
		id,
		error,
		hasDescription: Boolean(description),
		mixedId: mixed ? mixedId : undefined,
	});

	function toggle(optionValue: string) {
		if (value.includes(optionValue)) {
			onChange(value.filter((entry) => entry !== optionValue));
			return;
		}
		onChange([...value, optionValue]);
	}

	return (
		<FieldShell
			id={id}
			label={label}
			description={description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={className}
			describedById={mixed ? mixedId : undefined}
		>
			{name ? (
				<input
					type="hidden"
					name={name}
					value={value.join(",")}
					readOnly
				/>
			) : null}
			{mixed && mixedTooltip ? (
				<div className="mb-2 text-xs text-muted-foreground">{mixedTooltip}</div>
			) : null}
			{mixed ? (
				<p
					id={mixedId}
					data-testid={`${id}-mixed-placeholder`}
					className="mb-2 text-xs text-muted-foreground"
				>
					{mixedPlaceholder}
				</p>
			) : null}
			<div
				id={id}
				role="group"
				aria-label={label}
				aria-invalid={Boolean(error) || mixed ? true : undefined}
				aria-describedby={controlDescribedBy}
				onBlur={onBlur}
				className={cn(
					"grid gap-2",
					presentation === "tiles" ? "sm:grid-cols-2" : "grid-cols-1",
					mixed && "opacity-80",
				)}
			>
				{options.map((option) => {
					const selected = !mixed && value.includes(option.value);
					return (
						<button
							key={option.value}
							type="button"
							aria-pressed={selected}
							disabled={disabled}
							onClick={() => toggle(option.value)}
							className={cn(
								"relative flex cursor-pointer flex-col items-start rounded-lg border p-3 text-left transition-colors",
								selected
									? "border-primary bg-primary/5 ring-1 ring-primary"
									: "border-border hover:border-primary/50 hover:bg-muted/50",
								mixed && "border-dashed",
							)}
						>
							{selected ? (
								<CheckIcon
									className="absolute top-2 right-2 size-4 text-primary"
									aria-hidden
								/>
							) : null}
							<span className="font-medium text-foreground">
								{option.label}
							</span>
							{option.description ? (
								<span className="mt-0.5 text-xs text-muted-foreground">
									{option.description}
								</span>
							) : null}
						</button>
					);
				})}
			</div>
		</FieldShell>
	);
}
